import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Info, Megaphone, PartyPopper, Sparkles, TriangleAlert, Wrench, X } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

/* ------------------------------------------------------------------ */
/* Public site config: fetched once per page load, shared module cache */
/* ------------------------------------------------------------------ */

let cachedConfig;
let pending = null;
const listeners = new Set();

function loadSiteConfig() {
  if (cachedConfig !== undefined) return Promise.resolve(cachedConfig);
  if (!pending) {
    pending = api
      .get('/site-config', { timeout: 10000 })
      .then((res) => {
        cachedConfig = res && res.data && typeof res.data === 'object' ? res.data : null;
        return cachedConfig;
      })
      .catch(() => {
        cachedConfig = null;
        return null;
      })
      .finally(() => {
        pending = null;
        listeners.forEach((fn) => fn(cachedConfig));
      });
  }
  return pending;
}

/** Drop the cache (e.g. after an admin saves settings) and refetch for mounted consumers. */
export function clearSiteConfigCache() {
  cachedConfig = undefined;
  pending = null;
  if (listeners.size) loadSiteConfig();
}

/** `null` while loading or on error, otherwise `{ maintenance, announcement, flags }`. */
export function useSiteConfig() {
  const [config, setConfig] = useState(cachedConfig === undefined ? null : cachedConfig);
  useEffect(() => {
    let alive = true;
    const onChange = (c) => alive && setConfig(c);
    listeners.add(onChange);
    loadSiteConfig().then(onChange);
    return () => {
      alive = false;
      listeners.delete(onChange);
    };
  }, []);
  return config;
}

/* ------------------------------------------------------------------ */
/* Presentation                                                        */
/* ------------------------------------------------------------------ */

const TONES = {
  info: {
    icon: Info,
    cls: 'bg-navy text-on-navy',
    accent: 'bg-on-navy/15 text-sun',
    link: 'text-sun hover:text-on-navy',
    close: 'text-on-navy/70 hover:bg-on-navy/10 hover:text-on-navy',
  },
  success: {
    icon: PartyPopper,
    cls: 'bg-ok text-canvas',
    accent: 'bg-canvas/15 text-canvas',
    link: 'text-canvas underline decoration-canvas/50 hover:decoration-canvas',
    close: 'text-canvas/75 hover:bg-canvas/15 hover:text-canvas',
  },
  warning: {
    icon: TriangleAlert,
    cls: 'bg-sun text-sun-ink',
    accent: 'bg-sun-ink/10 text-sun-ink',
    link: 'text-sun-ink underline decoration-sun-ink/40 hover:decoration-sun-ink',
    close: 'text-sun-ink/70 hover:bg-sun-ink/10 hover:text-sun-ink',
  },
  promo: {
    icon: Sparkles,
    cls: 'bg-pop text-navy',
    accent: 'bg-navy/10 text-navy',
    link: 'text-navy underline decoration-navy/40 hover:decoration-navy',
    close: 'text-navy/70 hover:bg-navy/10 hover:text-navy',
  },
};

function safeLink(url) {
  const u = String(url || '').trim();
  if (!u) return null;
  if (u.startsWith('/') && !u.startsWith('//')) return { internal: true, href: u };
  if (/^https?:\/\//i.test(u)) return { internal: false, href: u };
  return null;
}

/**
 * Pure view of the banner, also used by the admin settings live preview.
 * Props: `announcement` ({ text, tone, linkUrl, linkLabel, dismissible }), optional `maintenance`
 * ({ enabled, message }) shown as a separate notice bar, `onDismiss`, `preview` (renders links inert).
 */
export function SiteBannerView({ announcement, maintenance, onDismiss, preview = false }) {
  const showMaint = Boolean(maintenance && maintenance.enabled);
  const text = announcement && String(announcement.text || '').trim();
  const showAnn = Boolean(announcement && text);
  if (!showMaint && !showAnn) return null;

  const tone = TONES[(announcement && announcement.tone) || 'info'] || TONES.info;
  const Icon = tone.icon || Megaphone;
  const link = showAnn ? safeLink(announcement.linkUrl) : null;
  const linkLabel = (announcement && String(announcement.linkLabel || '').trim()) || 'Learn more';
  const linkCls = `inline-flex shrink-0 items-center gap-1 font-bold no-underline transition-colors ${tone.link}`;

  let linkNode = null;
  if (link) {
    const inner = (
      <>
        {linkLabel} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </>
    );
    if (preview) linkNode = <span className={linkCls}>{inner}</span>;
    else if (link.internal)
      linkNode = (
        <Link to={link.href} className={linkCls}>
          {inner}
        </Link>
      );
    else
      linkNode = (
        <a href={link.href} target="_blank" rel="noopener noreferrer" className={linkCls}>
          {inner}
        </a>
      );
  }

  return (
    <div className="relative z-40">
      {showMaint && (
        <div role="status" className="border-b border-on-navy/10 bg-navy-deep text-on-navy">
          <div className="mx-auto flex max-w-6xl items-start gap-3 px-4 py-2.5 text-sm sm:items-center sm:px-6">
            <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-sun text-sun-ink sm:mt-0" aria-hidden="true">
              <Wrench className="h-3.5 w-3.5" />
            </span>
            <p className="min-w-0 flex-1 leading-snug">
              <b className="font-display font-bold text-sun">Maintenance in progress.</b>{' '}
              <span className="text-on-navy/80">{String(maintenance.message || '').trim() || 'Some features are temporarily unavailable. Please check back shortly.'}</span>
            </p>
          </div>
        </div>
      )}
      {showAnn && (
        <div role="region" aria-label="Announcement" className={tone.cls}>
          <div className="mx-auto flex max-w-6xl items-start gap-3 px-4 py-2.5 text-sm sm:items-center sm:px-6">
            <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full sm:mt-0 ${tone.accent}`} aria-hidden="true">
              <Icon className="h-3.5 w-3.5" />
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-x-3 gap-y-0.5 leading-snug sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
              <p className="min-w-0 break-words font-medium">{text}</p>
              {linkNode}
            </div>
            {announcement.dismissible !== false && (
              <button
                type="button"
                onClick={onDismiss}
                className={`-my-1 -mr-1.5 grid h-8 w-8 shrink-0 place-items-center rounded-full transition-colors ${tone.close}`}
                aria-label="Dismiss announcement"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const DISMISS_KEY = 'icd_banner_dismissed';

function announcementKey(a) {
  if (!a) return '';
  return String(a.id || `${a.text || ''}|${a.linkUrl || ''}`).slice(0, 200);
}

function readDismissed() {
  try {
    return window.localStorage.getItem(DISMISS_KEY) || '';
  } catch {
    return '';
  }
}

export default function SiteBanner() {
  const config = useSiteConfig();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(readDismissed);

  if (!config) return null;
  try {
    const ann = config.announcement && config.announcement.enabled ? config.announcement : null;
    const key = announcementKey(ann);
    const visibleAnn = ann && !(ann.dismissible !== false && dismissed && dismissed === key) ? ann : null;
    const isAdmin = user && user.role === 'admin';
    const maintenance = config.maintenance && config.maintenance.enabled && !isAdmin ? config.maintenance : null;

    const onDismiss = () => {
      setDismissed(key);
      try {
        window.localStorage.setItem(DISMISS_KEY, key);
      } catch {
        /* storage unavailable: dismissal lasts for this session only */
      }
    };

    return <SiteBannerView announcement={visibleAnn} maintenance={maintenance} onDismiss={onDismiss} />;
  } catch {
    return null;
  }
}
